import { createGtmEvent } from "@/lib/argus/client/gtm/gtm.ts";

type InputType =
  | "checkbox"
  | "date"
  | "email"
  | "file"
  | "number"
  | "password"
  | "radio"
  | "select"
  | "tel"
  | "text"
  | "textarea";

type Payload = {
  /** The action the used has taken, like navigation, focussing or submitting */
  action:
    | "click"
    | "focus"
    | "jumplink"
    | "mailto"
    | "navigation-external"
    | "navigation-internal"
    | "submit"
    | "tel";
  /** A path of parent containers to determine the location on the page */
  containers: string[];
  /** Tries to find a descriptive name */
  label: string;
  /** The type of element */
  type: "button" | "form" | "link" | `input-${InputType}`;
  /** The linked page or form action */
  url: string | null;
};

function getContainers(
  element: HTMLElement,
  containers: string[] = [],
): string[] {
  if (element.dataset.gtmContainer) {
    containers.unshift(element.dataset.gtmContainer);
  }
  const parent = element.parentElement?.closest<HTMLElement>(
    "[data-gtm-container]",
  );
  return !parent ? containers : getContainers(parent, containers);
}

function getButtonPayload(
  element: HTMLButtonElement | HTMLInputElement,
  event: Payload["action"],
): Payload {
  return {
    label:
      element.textContent?.trim() ||
      element.ariaLabel?.trim() ||
      element.value.trim() ||
      element.className,
    url:
      element.type === "submit"
        ? (element.closest<HTMLFormElement>("form[action]")?.action ?? null)
        : null,
    type: "button",
    action: element.type === "submit" ? "submit" : event,
    containers: getContainers(element),
  };
}

function getAnchorPayload(
  element: HTMLAnchorElement,
  event: Payload["action"],
): Payload {
  const action = (): Payload["action"] => {
    switch (true) {
      case element.protocol === "tel:":
        return "tel";
      case element.protocol === "mailto:":
        return "mailto";
      case element.hostname !== window.location.hostname:
        return "navigation-external";
      case element.pathname === window.location.pathname && !!element.hash:
        return "jumplink";
      case !!element.href:
        return "navigation-internal";
      default:
        return event;
    }
  };

  return {
    label:
      element.textContent?.trim() ||
      element.ariaLabel?.trim() ||
      element.className,
    url: element.href,
    type: "link",
    action: action(),
    containers: getContainers(element),
  };
}

function getInputPayload(
  element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  event: Payload["action"],
): Payload {
  const type = (): InputType => {
    switch (true) {
      case element instanceof HTMLInputElement:
        return element.type as
          | "checkbox"
          | "email"
          | "file"
          | "number"
          | "password"
          | "radio"
          | "tel"
          | "text";
      case element instanceof HTMLSelectElement:
        return "select";
      case element instanceof HTMLTextAreaElement:
        return "textarea";
      default:
        return "text";
    }
  };

  const labelFromElement = () => {
    if (element.id) {
      const labelEl = document.querySelector(
        `label[for="${CSS.escape(element.id)}"]`,
      );
      if (labelEl) {
        return labelEl.textContent;
      }
    }
    const labelledBy = element.getAttribute("aria-labelledby");
    if (labelledBy) {
      const labelEl = document.querySelector(`#${CSS.escape(labelledBy)}`);
      if (labelEl) {
        return labelEl.textContent;
      }
    }

    return null;
  };

  return {
    label:
      labelFromElement()?.trim() ||
      element.ariaLabel?.trim() ||
      element.getAttribute("placeholder")?.trim() ||
      element.name.trim() ||
      element.className,
    url: null,
    type: `input-${type()}`,
    action: event,
    containers: getContainers(element),
  };
}

function getFormPayload(
  element: HTMLFormElement,
  event: Payload["action"],
): Payload {
  const labelledBy = element.getAttribute("aria-labelledby");
  const labelFromElement = labelledBy
    ? document.querySelector(`#${CSS.escape(labelledBy)}`)?.textContent
    : null;

  return {
    label:
      labelFromElement?.trim() ||
      element.ariaLabel?.trim() ||
      element.className,
    url: element.action,
    type: "form",
    action: event,
    containers: getContainers(element),
  };
}

function addInteractionListener(
  event: string,
  callback: (target: HTMLElement) => Payload | undefined,
) {
  document.body.addEventListener(
    event,
    (event) => {
      const target = event.target;
      if (!target) {
        return;
      }
      const payload = callback(target as HTMLElement);
      if (payload) {
        void createGtmEvent("page_interaction", payload);
      }
    },
    { passive: true, capture: true },
  );
}

/**
 * Adds basic gtm events to all links, buttons, inputs and forms.
 * Gives some context so the element can be identified in GTM
 */
function initInteractionTracking() {
  addInteractionListener("click", (element) => {
    const target = element.closest("button,input,a[href]");
    if (
      target instanceof HTMLButtonElement ||
      (target instanceof HTMLInputElement &&
        ["submit", "button"].includes(target.type))
    ) {
      return getButtonPayload(target, "click");
    }

    if (target instanceof HTMLAnchorElement) {
      return getAnchorPayload(target, "click");
    }

    return undefined;
  });

  addInteractionListener("focus", (element) => {
    const target = element.closest("input,select,textarea");
    if (
      (target instanceof HTMLInputElement &&
        !["hidden", "submit", "button"].includes(target.type)) ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLTextAreaElement
    ) {
      return getInputPayload(target, "focus");
    }

    return undefined;
  });

  addInteractionListener("submit", (target) => {
    if (target instanceof HTMLFormElement) {
      return getFormPayload(target, "submit");
    }

    return undefined;
  });
}

export { initInteractionTracking };
